/**
 * Drafting Settings dialog (DSETTINGS / DDRMODES).
 *
 * AutoCAD-parity tabbed modal with four tabs:
 *   1. Snap and Grid   — SNAPMODE / GRIDMODE
 *   2. Polar Tracking  — POLARMODE
 *   3. Object Snap     — OSMODE bitmask
 *   4. Dynamic Input   — DYNMODE / DYNPROMPT / DYNPICOORDS / etc.
 *
 * Implemented as a plain div overlay (no Radix dependency) using the
 * same CSS and interaction patterns as DimStyleEditorDialog.
 *
 * Opening triggers:
 *   - Command line: DSETTINGS, DDRMODES, DDRMODE
 *   - Right-click on the "ДИН" status bar button (onContextMenu callback)
 *
 * Function dependency map:
 *   DSettingsDialog
 *     state: activeTab (which panel visible)
 *     renders one of: SnapGridTab / PolarTab / OSnapTab / DynamicInputTab
 *     Escape key → onOpenChange(false)
 *     overlay click → onOpenChange(false)
 */

import { useEffect, useState } from "react";

import { useT } from "../../i18n";
import { DynamicInputTab } from "./DynamicInputTab";
import { OSnapTab } from "./OSnapTab";
import { PolarTab } from "./PolarTab";
import { SnapGridTab } from "./SnapGridTab";

export type DSettingsTab = "snap" | "polar" | "osnap" | "dynamic-input";

export type DSettingsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Initial tab to open on — default "dynamic-input". */
  initialTab?: DSettingsTab;
};

export function DSettingsDialog({
  open,
  onOpenChange,
  initialTab = "dynamic-input",
}: DSettingsDialogProps) {
  const t = useT();
  const [activeTab, setActiveTab] = useState<DSettingsTab>(initialTab);

  // Reset to initialTab when dialog opens
  useEffect(() => {
    if (open) setActiveTab(initialTab);
  }, [open, initialTab]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onOpenChange(false);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onOpenChange]);

  if (!open) return null;

  const tabs: Array<{ id: DSettingsTab; label: string }> = [
    { id: "snap", label: t("dsettings.tab.snap") },
    { id: "polar", label: t("dsettings.tab.polar") },
    { id: "osnap", label: t("dsettings.tab.osnap") },
    { id: "dynamic-input", label: t("dsettings.tab.dyninput") },
  ];

  return (
    <div
      className="dsettings-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onOpenChange(false);
      }}
    >
      <div
        className="dsettings-content"
        role="dialog"
        aria-modal="true"
        aria-label={t("dsettings.title")}
        data-testid="dsettings-dialog"
      >
        <div className="dsettings-title" id="dsettings-title">
          {t("dsettings.title")}
        </div>

        {/* ── Tab list ── */}
        <div className="dsettings-tabs" role="tablist">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              className={
                "dsettings-tab" + (activeTab === tab.id ? " is-active" : "")
              }
              onClick={() => setActiveTab(tab.id)}
              data-testid={`dsettings-tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Tab panels ── */}
        <div className="dsettings-panel" role="tabpanel">
          {activeTab === "snap" ? <SnapGridTab /> : null}
          {activeTab === "polar" ? <PolarTab /> : null}
          {activeTab === "osnap" ? <OSnapTab /> : null}
          {activeTab === "dynamic-input" ? <DynamicInputTab /> : null}
        </div>

        {/* ── Footer ── */}
        <div className="dsettings-footer">
          <button
            type="button"
            className="dsettings-btn primary"
            onClick={() => onOpenChange(false)}
            data-testid="dsettings-ok"
          >
            {t("dsettings.ok")}
          </button>
          <button
            type="button"
            className="dsettings-btn"
            onClick={() => onOpenChange(false)}
            data-testid="dsettings-cancel"
          >
            {t("dsettings.cancel")}
          </button>
        </div>
      </div>
    </div>
  );
}
